import type { TokenProvider } from "./tokenProvider";

/**
 * In-tab single-flight wrapper around `TokenProvider.tryRefresh`.
 *
 * Multiple concurrent 401s inside this tab resolve on ONE `tryRefresh()` call
 * — the first caller kicks off the refresh, subsequent callers await the same
 * promise. Cross-tab dedup lives in `@linkhr/auth` via `BroadcastChannel` +
 * `navigator.locks` (D-12). This bridge just makes sure the api-client
 * middleware never double-fires even if `@linkhr/auth.tryRefresh` is not itself
 * deduped.
 *
 * @example
 *   const refresh = createSingleFlightRefresh(tokenProvider);
 *   // Request A (401) and B (401) both in-flight → both await the same promise:
 *   await Promise.all([refresh(), refresh()]); // one actual tryRefresh call
 */
export function createSingleFlightRefresh(provider: TokenProvider): () => Promise<string> {
  let inflight: Promise<string> | null = null;
  return () => {
    if (inflight) return inflight;
    inflight = provider.tryRefresh().finally(() => {
      inflight = null;
    });
    return inflight;
  };
}
